import { useCallback, useEffect, useMemo, useState } from 'react';
import { AttachmentResult } from '@sdir/httpclient/lib/clients/aps/application';
import { AttachmentUpdate, Loadable } from '../../types';
import { useCurrentApplication } from './useCurrentApplication';
import { useAppAttachmentFile } from './useAppAttachmentFile';
import { VirtualFile } from '../../types/filegroup';

type FileOrAttachment =
  | { isAttached: false; newFile: File }
  | { isAttached: true; attachment: AttachmentResult; newFile: File | null };

type AvailableFile = VirtualFile & FileOrAttachment;

export type AttachmentGroupChanges = {
  updatedAttachments: AttachmentUpdate[];
  removedAttachments: string[];
  newFiles: File[];
};

type AttachmentGroupWorkspaceResult = {
  groupQualificationUri: string | null;
  attachmentsForGroup: AttachmentResult[];
  changes: AttachmentGroupChanges;
  availableFiles: VirtualFile[];
  activeFileOption: VirtualFile | null;
  activeFile: Loadable<File> | null;
  handleFileSelected: (selected: VirtualFile) => void;
  handleAddNewFiles: (files: File[]) => AttachmentGroupChanges;
  handleUpdateActiveFile: (newFile: File) => AttachmentGroupChanges;
  handleRemoveActiveFile: () => AttachmentGroupChanges;
  handleRemoveFile: (file: VirtualFile) => AttachmentGroupChanges;
};

export const useAttachmentGroupWorkspace = (
  group: string,
  initialState?: AttachmentGroupChanges
): AttachmentGroupWorkspaceResult => {
  const { application } = useCurrentApplication();

  const [activeFileIndex, setActiveFileIndex] = useState(0);
  const [changes, setChanges] = useState<AttachmentGroupChanges>(
    initialState || {
      updatedAttachments: [],
      removedAttachments: [],
      newFiles: [],
    }
  );

  const attachmentsForGroup = useMemo(
    () => application.result?.attachments?.filter((a) => a.group === group) || [],
    [application.result?.attachments, group]
  );

  const groupQualificationUri = attachmentsForGroup?.[0]?.simulatorResultItemUri || null;

  const attachedGroupFiles = useMemo((): AvailableFile[] => {
    // combine already attached files and files added by user
    const { removedAttachments, updatedAttachments } = changes;
    const nonRemovedAttachments =
      attachmentsForGroup?.filter((a) => !removedAttachments.find((r) => r === a.uid)) || [];
    return nonRemovedAttachments.map((a) => {
      const updateMatch = updatedAttachments.find((u) => u.attachmentUid === a.uid);
      return {
        isAttached: true,
        name: updateMatch?.newFile?.name || a.blobName || '',
        attachment: a,
        newFile: updateMatch?.newFile || null,
      };
    });
  }, [attachmentsForGroup, changes]);

  const allGroupFiles = useMemo(() => {
    return attachedGroupFiles.concat(
      changes.newFiles.map((n): AvailableFile => ({ isAttached: false, newFile: n, name: n.name }))
    );
  }, [attachedGroupFiles, changes]);

  const activeGroupFile = useMemo<AvailableFile | null>(
    () => allGroupFiles[activeFileIndex] || null,
    [allGroupFiles, activeFileIndex]
  );

  const applyWorkspaceStateChange = useCallback(
    (changeUpdate: Partial<AttachmentGroupChanges>) => {
      const newChanges = { ...changes, ...changeUpdate };
      setChanges(newChanges);
      return newChanges;
    },
    [changes]
  );

  const handleAddNewFiles = useCallback(
    (newFiles: File[]): AttachmentGroupChanges => {
      const newChanges = applyWorkspaceStateChange({
        newFiles: [...changes.newFiles, ...newFiles],
      });
      const addedAtIndex =
        attachmentsForGroup.length +
        newChanges.newFiles.length -
        newChanges.removedAttachments.length -
        1;
      setActiveFileIndex(addedAtIndex);
      return newChanges;
    },
    [applyWorkspaceStateChange, attachmentsForGroup.length, changes.newFiles]
  );

  const handleFileSelected = useCallback(
    (selectedFile: VirtualFile) => {
      const matchingIndex = allGroupFiles.findIndex((x) => x === selectedFile);
      if (matchingIndex < 0) return;
      setActiveFileIndex(matchingIndex);
    },
    [allGroupFiles]
  );

  const handleUpdateActiveFile = useCallback(
    (updatedFile: File): AttachmentGroupChanges => {
      if (!activeGroupFile) return changes;

      if (!activeGroupFile.isAttached) {
        return applyWorkspaceStateChange({
          newFiles: changes.newFiles.map((f) => {
            if (f !== activeGroupFile.newFile) {
              return updatedFile;
            }
            return f;
          }),
        });
      }

      return applyWorkspaceStateChange({
        updatedAttachments: [
          ...changes.updatedAttachments.filter(
            (u) => u.attachmentUid !== activeGroupFile.attachment.uid
          ),
          {
            attachmentUid: activeGroupFile.attachment.uid || '',
            newFile: updatedFile,
          },
        ],
      });
    },
    [activeGroupFile, applyWorkspaceStateChange, changes]
  );

  const handleRemoveActiveFile = useCallback((): AttachmentGroupChanges => {
    if (!activeGroupFile) return changes;

    let newChanges: AttachmentGroupChanges;
    if (!activeGroupFile.isAttached) {
      newChanges = applyWorkspaceStateChange({
        newFiles: changes.newFiles.filter((f) => f !== activeGroupFile.newFile),
      });
    } else {
      newChanges = applyWorkspaceStateChange({
        removedAttachments: [...changes.removedAttachments, activeGroupFile.attachment.uid || ''],
      });
    }

    if (activeFileIndex > 0) setActiveFileIndex(activeFileIndex - 1);

    return newChanges;
  }, [activeFileIndex, activeGroupFile, applyWorkspaceStateChange, changes]);

  const handleRemoveFile = useCallback(
    (file: VirtualFile): AttachmentGroupChanges => {
      if (!file) return changes;
      const newRemovals =
        attachmentsForGroup && attachmentsForGroup.length > 0
          ? attachmentsForGroup.filter((i) => i.blobName === file.name)?.map((j) => j.uid || '')
          : [];
      const newChanges: AttachmentGroupChanges = applyWorkspaceStateChange({
        newFiles: changes.newFiles.filter((f) => f.name !== file.name),
        removedAttachments:
          newRemovals?.length > 0
            ? [...changes.removedAttachments, ...newRemovals]
            : changes.removedAttachments,
      });
      if (activeFileIndex > 0) setActiveFileIndex(0);
      return newChanges;
    },
    [activeFileIndex, attachmentsForGroup, applyWorkspaceStateChange, changes]
  );

  useEffect(() => {
    applyWorkspaceStateChange({
      updatedAttachments: changes.updatedAttachments.filter((u) =>
        attachmentsForGroup.find((a) => a.uid === u.attachmentUid)
      ),
      removedAttachments: changes.removedAttachments.filter((r) =>
        attachmentsForGroup.find((a) => a.uid === r)
      ),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachmentsForGroup]);

  const downloadedActiveAttachmentFile = useAppAttachmentFile(
    activeGroupFile && activeGroupFile.isAttached ? activeGroupFile.attachment : null
  );
  const activeFile = useMemo(() => {
    if (!activeGroupFile) return null;
    if (activeGroupFile.newFile) {
      return { result: activeGroupFile.newFile };
    }
    if (activeGroupFile.isAttached) {
      return downloadedActiveAttachmentFile;
    }
    return null;
  }, [activeGroupFile, downloadedActiveAttachmentFile]);

  return {
    activeFile,
    activeFileOption: activeGroupFile,
    availableFiles: allGroupFiles,
    changes,
    groupQualificationUri,
    attachmentsForGroup,
    handleFileSelected,
    handleAddNewFiles,
    handleUpdateActiveFile,
    handleRemoveActiveFile,
    handleRemoveFile,
  };
};
